import React, { useEffect, useRef, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { marketingColors as C, marketingFonts as F, visualTokens as V } from '../../../theme_landing_page_2';
import Eyebrow from '../Eyebrow';

/**
 * Chapter-divider windmill reveal: a 4-blade mill sits still until the section scrolls into
 * view, then turns to land its active sail pointing right and reveals the chapter title below
 * it. Adaptation of Claude Design's "RentoraMillChapter" component (IntersectionObserver-gated
 * reveal instead of a scroll-scrub, unlike MillosAgentsScroll/MillosPricingHero).
 */

const BLADE_COLORS = [V.brandBlue, V.brandCyan, V.brandViolet, V.brandTeal];

interface Props {
  agent: 'compliance' | 'accounting';
}

const MillosChapterHeader: React.FC<Props> = ({ agent }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const prefersReduced = useMediaQuery('(prefers-reduced-motion: reduce)');

  useEffect(() => {
    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      setOpen(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setOpen(true);
          io.disconnect();
        }
      },
      { threshold: 0.45 }
    );
    if (rootRef.current) io.observe(rootRef.current);
    return () => io.disconnect();
  }, [prefersReduced]);

  const acct = agent === 'accounting';
  const active = acct ? 3 : 0;
  const rot = open ? (acct ? 520 : 430) : 0;
  const glow = acct ? V.brandTealAlpha26 : V.brandBlueAlpha22;

  const blades = BLADE_COLORS.map((c, i) => ({
    c,
    t: `rotate(${i * 90}deg)` + (i === active && open ? ' translate(4px,-10px) scale(1.14)' : ''),
    o: !open || i === active ? 1 : 0.22,
  }));

  return (
    <Box
      ref={rootRef}
      sx={{ position: 'relative', overflow: 'hidden', padding: { xs: '56px 24px 32px', md: '72px 40px 40px' }, textAlign: 'center', fontFamily: F.body, color: C.navy,
        '@media (prefers-reduced-motion: reduce)': { '& *': { transition: 'none !important' } },
      }}
    >
      <Box sx={{ position: 'relative', width: 'min(180px,36vw)', height: 'min(180px,36vw)', margin: '0 auto 24px' }}>
        <Box sx={{ position: 'absolute', inset: '-18%', borderRadius: '50%', background: `radial-gradient(circle,${glow},transparent 66%)`, opacity: open ? 1 : 0, transition: 'opacity 1s ease .5s' }} />
        <svg aria-hidden="true" viewBox="-65 -65 130 130" style={{ position: 'relative', width: '100%', height: '100%', overflow: 'visible', transform: `rotate(${rot}deg)`, transition: 'transform 1.6s cubic-bezier(.35,0,.15,1)' }}>
          {blades.map((b, i) => (
            <g key={i} style={{ transform: b.t, opacity: b.o, transition: 'opacity .7s ease 1s' }}>
              <path d="M-4 -4 L-10 -42 C 10 -48 30 -40 40 -26 Z" fill={b.c} />
              <path d="M-4 -4 L-10 -42 L6 -30 Z" fill={V.black} opacity={0.2} />
            </g>
          ))}
        </svg>
      </Box>
      <Box sx={{ position: 'relative' }}>
        <Eyebrow>
          {acct ? 'Agent 02' : 'Agent 01'}
        </Eyebrow>
        <Box component="h2" sx={{ fontFamily: F.display, fontSize: 'clamp(2.6rem,6vw,4rem)', letterSpacing: '-0.035em', lineHeight: 1.06, mt: 2, mb: 2 }}>
          {acct ? 'Accounting' : 'Certificates'}
        </Box>
        <Box component="p" sx={{ maxWidth: '40rem', margin: '16px auto 0', fontSize: 17, lineHeight: 1.6, color: C.body }}>
          {acct
            ? 'Beyond recording. Into preparation. MillOS matches rent, organises invoices and expenses, and prepares quarterly figures for your accountant.'
            : 'Beyond tracking. Into action. MillOS handles each certificate from the day it arrives to the day its replacement is filed, so renewals never depend on your memory.'}
        </Box>
      </Box>
    </Box>
  );
};

export default MillosChapterHeader;
